import React, { useCallback } from "react";
import { makeStyles, Theme, ThemeProvider } from "@material-ui/core/styles";
import { useTenantImage, useTenant, TenantsConfig } from "@ploy-ui/tenants";

import merge from "lodash/merge";
import { ImageFit } from "@ploy-ui/core";

const useBannerStyles = makeStyles<
	Theme,
	{
		bannerBackgroundImage?: string | null;
		useTransparentBackground?: boolean;
	}
>(
	{
		banner: {
			height: "335px",
			position: "absolute",
			backgroundPosition: "top",
			top: 0,
			left: 0,
			width: "100%",
			zIndex: -1
		}
	},
	{
		name: "DployFormViewsBanner"
	}
);

const GetLogoCenteringIfVWMB = (tenantConfig: TenantsConfig) => {
	const tenant = tenantConfig.tenant;
	const noTenant = (tenant === undefined || tenant === null);
	if (noTenant) return {};

	const notVWMB = tenant.tenantName !== "VWMB";
	if (notVWMB) return {};
	return { paddingTop: "2rem" }
}

export function LayoutWithBanner(
	props: React.PropsWithChildren<{ disableBanner?: boolean }>
) {
	const { children, disableBanner } = props;

	let bannerBackgroundImage = useTenantImage("form_web_header");
	if (disableBanner) bannerBackgroundImage = null;
	const hasBanner = bannerBackgroundImage != null;

	const classes = useBannerStyles({
		bannerBackgroundImage
	});
	// VWMB directweb landing page looks a bit different now, using the logo as "header image" and a background image that is just a color.
	// It resulted in some bad centering.

	const tenantConfig: TenantsConfig = useTenant();
	let paddingToCenterLogoIfVWMB = GetLogoCenteringIfVWMB(tenantConfig);

	const formWebThemeOverride = useCallback(
		(theme: Theme): Theme => ({
			...theme,
			/**
			 * I use merge() here to ensure we keep preexisting overrides and _all_ of their properties (deep merge), if you want to override some components completely, use eg.
			 * @example
			 * {...,
			 * 	overrides: {
			 * 		...merge({}, theme.overrides, {...existing code}),
			 * 		...{my specific overrides}
			 * 	}
			 * }
			 */
			overrides: merge({}, theme.overrides, {
				DirWebStepper: {
					...(hasBanner
						? {
								label: {
									color: theme.palette.primary.contrastText,

									"&$activeLabel": {
										color: theme.palette.primary.contrastText
									},

									"&$completedLabel": {
										color: theme.palette.primary.contrastText
									}
								},
								activeLabel: {},
							completedLabel: {},
							root: paddingToCenterLogoIfVWMB
						  }
						: undefined)
				},
				DirWebStepperConnector: {
					...(hasBanner
						? {
								line: {
									borderColor: theme.palette.primary.contrastText
								}
						  }
						: undefined)
				},
				DirWebStepperStepIcon: {
					...(hasBanner
						? {
								root: {
									color: theme.palette.primary.contrastText,
									borderColor: theme.palette.primary.contrastText
								}
						  }
						: undefined)
				}
			})
		}),
		[hasBanner, paddingToCenterLogoIfVWMB]
	);

	return (
		<ThemeProvider theme={formWebThemeOverride}>
			<ImageFit
				className={classes.banner}
				src={bannerBackgroundImage}
				alt="bannerBackgroundImage"
				fit="cover"
				//undefined to not override default position value in classes.banner. This will allow tenants to override.
				//TODO: Use this position props when developing image position support for brands
				position="undefined"
			/>
			{children}
		</ThemeProvider>
	);
}
